import { TenantRoleDto } from '@admin/app/api'
import { RoleStore } from '@admin/stores/role.store'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ConfirmationDialog,
  OptionsButton,
  TomTable,
  TomTableActionsProps,
  tomTableColumnFilterPresets,
  useTomTable,
} from '@tom-ui/ui'
import { appStore, useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useRoleMessageHandler } from '../../hooks/useRolesMessageHandler'

interface Props {
  store: RoleStore
}

export const RolesTable = observer(({ store }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const [deleteItems, setDeleteItems] = useState<{ id: number; name: string }[]>([])
  const { getExceptionMessage, getDeleteSuccessMessage } = useRoleMessageHandler()

  const handleDelete = async () => {
    if (!deleteItems.length) return
    try {
      const requests = deleteItems.map(item => store.deleteRole(item.id))

      await Promise.all(requests)
      appStore.setShowAlert('success', getDeleteSuccessMessage())
      setDeleteItems([])
    } catch (error: any) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const columns = useMemo<MRT_ColumnDef<TenantRoleDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'description',
        header: t('description', 'Description'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'permissions',
        header: t('permissions', 'Permissions'),
        accessorFn: row => row.permissions.map(permission => t(permission)).join(', '),
        ...tomTableColumnFilterPresets.searchMandatory(),
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
        },
      },
    ],
    [t],
  )

  const tableActions = useMemo<TomTableActionsProps<TenantRoleDto>[]>(
    () => [
      {
        label: t('delete', 'Delete'),
        onClick: rows => {
          const itemsToDelete = rows.map(row => ({
            id: row.original.id,
            name: row.original.name,
          }))
          setDeleteItems(itemsToDelete)
        },
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('admin/roles')

  const table = useTomTable<TenantRoleDto>(
    {
      columns,
      data: store.data,
      enableExpanding: false,
      layoutMode: 'grid',
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () => navigate(`${row.original.id}`),
            testId: `edit-${row.original.id}`,
          },
          {
            label: t('delete', 'Delete'),
            onClick: () => setDeleteItems([{ id: row.original.id, name: row.original.name }]),
            testId: `delete-${row.original.id}`,
          },
        ]
        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='role-options-btn'
          />
        )
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 202px)',
          minHeight: 'calc(100vh - 202px)',
        },
      },
    },
    tableActions,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <>
      <TomTable table={table} />
      <ConfirmationDialog
        open={deleteItems.length > 0}
        title={
          deleteItems.length === 1
            ? t('deleteRole', 'Delete role?')
            : t('deleteRoles', 'Delete roles?')
        }
        message={
          deleteItems.length === 1
            ? t('deleteRoleMessage', 'Role {role} will be deleted.', {
                role: deleteItems[0].name,
              })
            : t('deleteRolesMessage', 'Roles {roles} will be deleted.', {
                roles: deleteItems.map(item => item.name).join(', '),
              })
        }
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('noCancel', 'No, cancel')}
        maxWidth='xs'
        onConfirm={() => handleDelete()}
        onClose={() => setDeleteItems([])}
      />
    </>
  )
})
